/**
 * UnreadBadge Component - HYB Shop Chat
 * Notification count badge with pulse animation option
 *
 * Design: Vibrant gradient with optional pulsing glow effect
 */


import { cn } from '../../../utils/responsive';

interface UnreadBadgeProps {
  /** Number of unread items */
  count: number;
  /** Size variant */
  size?: 'sm' | 'md';
  /** Show pulse animation */
  pulse?: boolean;
  /** Maximum count to display (shows "max+" for higher) */
  maxCount?: number;
  /** Additional CSS classes */
  className?: string;
}

const SIZE_CONFIG = {
  sm: {
    container: 'h-5 min-w-[20px] px-1.5 text-[10px]',
    pulse: 'h-5 w-5',
  },
  md: {
    container: 'h-6 min-w-[24px] px-2 text-xs',
    pulse: 'h-6 w-6',
  },
};

export function UnreadBadge({
  count,
  size = 'sm',
  pulse = false,
  maxCount = 99,
  className,
}: UnreadBadgeProps) {
  // Don't render if count is 0 or negative
  if (count <= 0) return null;

  const config = SIZE_CONFIG[size];
  const displayCount = count > maxCount ? `${maxCount}+` : count.toString();

  return (
    <div className={cn('relative inline-flex', className)}>
      {/* Pulse Animation Ring */}
      {pulse && (
        <span
          className={cn(
            'absolute inset-0 rounded-full',
            'bg-red-500 dark:bg-red-600',
            'animate-ping opacity-75',
            config.pulse
          )}
          aria-hidden="true"
        />
      )}

      {/* Badge */}
      <span
        className={cn(
          'relative inline-flex items-center justify-center',
          'rounded-full font-bold',
          'bg-gradient-to-br from-red-500 to-orange-500',
          'dark:from-red-600 dark:to-orange-600',
          'text-white',
          'shadow-sm',
          'transition-transform duration-200 ease-spring',
          'hover:scale-110',
          config.container
        )}
        role="status"
        aria-label={`${count} ข้อความที่ยังไม่ได้อ่าน`}
      >
        {displayCount}
      </span>
    </div>
  );
}

export default UnreadBadge;
